import React, { useState, useMemo } from 'react'
import { useMeasurement } from '@/contexts/measurementContext'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { SkillEnum, CurrencyEnum, QuestEnum } from '@/types'
import { formatDuration } from '@/utils'
import { Checkbox } from '@nextui-org/react'
import SKILLS from '@/constants/skills'
import CURRENCIES from '@/constants/currencies'

type MetricCategory = 'quest' | 'currency' | 'skillPoints' | 'skillLevels'
type MetricKey = QuestEnum | CurrencyEnum | SkillEnum | 'completedQuestCount'

interface MetricFilter {
  category: MetricCategory
  keys: Set<MetricKey>
}

// Fixed color palette for skills
const SKILL_COLORS = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#2e8b57', // sea-green
  '#9333ea', // purple-600
  '#ea580c', // orange-600
  '#0891b2', // cyan-600
  '#be123c', // rose-700
  '#1d4ed8', // blue-700
  '#15803d', // green-700
  '#7c2d12', // orange-900
  '#86198f', // fuchsia-800
  '#115e59', // teal-800
  '#b45309', // amber-700
  '#1e3a8a', // blue-900
  '#365314', // lime-900
  '#831843' // pink-800
]

export const MeasureScreen: React.FC = () => {
  const { snapshots, isRecording, clearSnapshots } = useMeasurement()
  const [selectedMetrics, setSelectedMetrics] = useState<MetricFilter>({
    category: 'quest',
    keys: new Set(['completedQuestCount'])
  })

  // Get a fixed color for a skill based on its index
  const getSkillColor = (skillKey: string) => {
    const skillKeys = Object.keys(SKILLS)
    const index = skillKeys.indexOf(skillKey)
    return SKILL_COLORS[index % SKILL_COLORS.length]
  }

  // Get a fixed color for a currency based on its index
  const getCurrencyColor = (currencyKey: string) => {
    const currencyKeys = Object.keys(CURRENCIES)
    const index = currencyKeys.indexOf(currencyKey)
    return SKILL_COLORS[index % SKILL_COLORS.length]
  }

  // Sample snapshots if there are too many
  const sampledSnapshots = useMemo(() => {
    let step = 1
    if (snapshots.length > 200) step = 4
    else if (snapshots.length > 100) step = 2

    return snapshots.filter((_, index) => index % step === 0)
  }, [snapshots])

  const handleCategoryChange = (category: MetricCategory) => {
    setSelectedMetrics({
      category,
      keys: new Set()
    })
  }

  const handleMetricToggle = (key: MetricKey) => {
    const newKeys = new Set(selectedMetrics.keys)
    if (newKeys.has(key)) {
      newKeys.delete(key)
    } else {
      newKeys.add(key)
    }
    setSelectedMetrics({
      ...selectedMetrics,
      keys: newKeys
    })
  }

  const renderFilters = () => {
    const categories: { key: MetricCategory; label: string }[] = [
      { key: 'quest', label: 'Quests' },
      { key: 'currency', label: 'Currencies' },
      { key: 'skillPoints', label: 'Skill Points' },
      { key: 'skillLevels', label: 'Skill Levels' }
    ]

    return (
      <div className="mb-4 space-y-4">
        <div className="flex gap-4">
          {categories.map(({ key, label }) => (
            <button
              key={key}
              className={`px-4 py-2 rounded ${
                selectedMetrics.category === key
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => handleCategoryChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedMetrics.category === 'quest' && (
            <Checkbox
              isSelected={selectedMetrics.keys.has('completedQuestCount')}
              onValueChange={() => handleMetricToggle('completedQuestCount')}
              size="sm"
            >
              Completed Quests
            </Checkbox>
          )}
          {selectedMetrics.category === 'currency' &&
            Object.entries(CURRENCIES).map(([key, currency]) => (
              <Checkbox
                key={key}
                isSelected={selectedMetrics.keys.has(key as CurrencyEnum)}
                onValueChange={() => handleMetricToggle(key as CurrencyEnum)}
                size="sm"
              >
                {currency.name}
              </Checkbox>
            ))}
          {(selectedMetrics.category === 'skillPoints' || selectedMetrics.category === 'skillLevels') &&
            Object.entries(SKILLS).map(([key, skill]) => (
              <Checkbox
                key={key}
                isSelected={selectedMetrics.keys.has(key as SkillEnum)}
                onValueChange={() => handleMetricToggle(key as SkillEnum)}
                size="sm"
              >
                {skill.abbreviation}
              </Checkbox>
            ))}
        </div>
      </div>
    )
  }

  const renderChart = () => {
    if (sampledSnapshots.length === 0) {
      return <div className="text-gray-500">No data available. Start autoplay to begin recording.</div>
    }

    // Calculate tick interval based on data length
    const tickInterval = Math.max(Math.floor(sampledSnapshots.length / 5), 1)

    return (
      <div style={{ width: '100%', height: '600px' }} className="border border-gray-200 rounded-lg p-4 bg-white">
        <ResponsiveContainer>
          <LineChart data={sampledSnapshots} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="gameMinutes"
              tickFormatter={(value) => formatDuration(value)}
              label={{ value: 'Game Time', position: 'insideBottom', offset: -5 }}
              stroke="#666"
              interval={tickInterval}
            />
            <XAxis
              dataKey="realMinutes"
              tickFormatter={(value) => formatDuration(value)}
              label={{ value: 'Real Time', position: 'insideBottom', offset: 15 }}
              xAxisId="realTime"
              stroke="#666"
              interval={tickInterval}
            />
            <YAxis stroke="#666" />
            <Tooltip
              formatter={(value: number, name: string) => [value.toFixed(2), name]}
              labelFormatter={(value: number) => `Game Time: ${formatDuration(value)}`}
              contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
            />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            {selectedMetrics.category === 'quest' && selectedMetrics.keys.has('completedQuestCount') && (
              <Line
                type="stepAfter"
                dataKey="completedQuestCount"
                stroke="#8884d8"
                name="Completed Quests"
                strokeWidth={3}
                dot={false}
              />
            )}
            {selectedMetrics.category === 'currency' &&
              Array.from(selectedMetrics.keys).map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={`currencies.${key}`}
                  stroke={getCurrencyColor(key as string)}
                  name={CURRENCIES[key as CurrencyEnum].name}
                  strokeWidth={3}
                  dot={false}
                  connectNulls
                />
              ))}
            {selectedMetrics.category === 'skillPoints' &&
              Array.from(selectedMetrics.keys).map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={`skillPoints.${key}`}
                  stroke={getSkillColor(key as string)}
                  name={`${SKILLS[key as SkillEnum].name} Points`}
                  strokeWidth={3}
                  dot={false}
                  connectNulls
                />
              ))}
            {selectedMetrics.category === 'skillLevels' &&
              Array.from(selectedMetrics.keys).map((key) => (
                <Line
                  key={key}
                  type="stepAfter"
                  dataKey={`skillLevels.${key}`}
                  stroke={getSkillColor(key as string)}
                  name={`${SKILLS[key as SkillEnum].name} Level`}
                  strokeWidth={3}
                  dot={false}
                  connectNulls
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Game Measurements</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {isRecording ? (
              <span className="text-green-500">Recording... ({snapshots.length} snapshots)</span>
            ) : (
              <span>Not recording (start autoplay to record)</span>
            )}
          </div>
          {snapshots.length > 0 && (
            <button
              onClick={clearSnapshots}
              className="px-3 py-1 text-sm bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {renderFilters()}
      {renderChart()}
    </div>
  )
}
